/**
 * DOM contract for end-to-end tests.
 *
 * Playwright resolves locators through `data-testid` (its `testIdAttribute`
 * option). MUI spreads unknown props onto a component's *root* element, so on a
 * `TextField` a plain `data-testid` lands on the `.MuiFormControl-root` wrapper
 * rather than the focusable `<input>`, and `fill()` / `press()` fail with
 * "element is not an <input>". Shared wrappers therefore place the id
 * deliberately through the helpers below instead of spreading it.
 *
 * Rules the wrappers follow, so specs can rely on them:
 * - a field's `data-testid` is on the `<input>` / `<textarea>` itself;
 * - a button's `data-testid` is on the `<button>`;
 * - a container's `data-testid` is on the outermost element of that container,
 *   and its children use `${id}-<part>` so one prefix scopes a whole widget.
 */

/** Attribute name, kept in one place so `playwright.config.ts` can mirror it. */
export const TEST_ID_ATTRIBUTE = "data-testid";

/** Props bag carrying an optional test id. */
export interface ITestIdProps {
  "data-testid"?: string;
}

/**
 * Renders `{ "data-testid": id }`, or an empty object when `id` is undefined so
 * an unset id never emits an empty attribute.
 */
export function testIdAttr(id?: string): ITestIdProps {
  return id ? { [TEST_ID_ATTRIBUTE]: id } : {};
}

/**
 * Joins id segments with `-`, skipping empty ones, and returns `undefined` when
 * nothing usable is left. `buildTestId("client", "name")` -> `"client-name"`.
 */
export function buildTestId(
  ...parts: (string | number | false | null | undefined)[]
): string | undefined {
  const joined = parts
    .filter((part) => part !== undefined && part !== null && part !== false)
    .map((part) => String(part).trim())
    .filter((part) => part.length > 0)
    .join("-");

  return joined.length > 0 ? joined : undefined;
}

/**
 * Turns an arbitrary value - an API url, an entity name, a translated label -
 * into a slug safe to embed in a test id: lower case, with every run of
 * non-alphanumeric characters collapsed to a single `-`.
 *
 * Used to derive stable default ids from something the component already knows
 * (a grid's `url`, a drawer item's key) so a screen gets usable ids without
 * every call site having to invent them.
 */
export function slugTestId(value?: string | null): string | undefined {
  if (!value) return undefined;

  const slug = value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug.length > 0 ? slug : undefined;
}

/**
 * Slugs a **form field name** into a test id.
 *
 * Field names in this codebase are camelCase (`clientName`, `postalCode`,
 * `buildingNumber`), so plain {@link slugTestId} would flatten them to
 * `clientname`. This splits the camelCase boundaries first, keeping derived
 * ids in the lower-kebab-case the convention calls for: `clientName` ->
 * `client-name`, `POBox` -> `po-box`, `area.subDivisionName` ->
 * `area-sub-division-name`.
 *
 * Use this wherever an id is derived from a field name. {@link slugTestId}
 * stays as-is for urls, i18n keys and labels, which are already dash- or
 * slash-separated.
 */
export function slugFieldName(value?: string | null): string | undefined {
  if (!value) return undefined;

  const spaced = value
    .toString()
    // clientName -> client-Name
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    // POBox -> PO-Box, so an acronym run does not swallow the next word
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2");

  return slugTestId(spaced);
}

/**
 * Derives a field's test id from the form field `name` it is registered
 * under, so a screen gets usable ids without every call site inventing one.
 *
 * `inputProps` is typically a react-hook-form `register("clientName")`
 * result, whose `name` is a stable, untranslated identifier - unlike a label,
 * which changes with the locale. Returns `undefined` when nothing usable is
 * there, in which case the wrapper emits no attribute at all.
 */
export function deriveInputTestId(
  explicitId: string | undefined,
  ...sources: (Record<string, any> | undefined)[]
): string | undefined {
  if (explicitId) return explicitId;

  for (const source of sources) {
    const name = source?.name;
    if (typeof name === "string") {
      const slug = slugFieldName(name);
      if (slug) return slug;
    }
  }

  return undefined;
}

/**
 * Merges a test id into MUI's `inputProps` (the `htmlInput` slot), so the id
 * lands on the focusable control. Any existing `inputProps` - commonly a
 * react-hook-form `register(...)` result - are preserved.
 */
export function withInputTestId(
  id: string | undefined,
  inputProps?: Record<string, any>
): Record<string, any> | undefined {
  if (!id) return inputProps;
  return { ...(inputProps ?? {}), [TEST_ID_ATTRIBUTE]: id };
}

/**
 * Same as {@link withInputTestId} for MUI's `InputProps.inputProps` escape
 * hatch. Components that accept a caller-supplied `InputProps` merge through
 * both paths, so the id reaches the `<input>` whichever of the two MUI resolves
 * last - they address the same element, so the attribute is never duplicated.
 */
export function withNestedInputTestId(
  id: string | undefined,
  InputProps?: Record<string, any>
): Record<string, any> | undefined {
  if (!id) return InputProps;
  return {
    ...(InputProps ?? {}),
    inputProps: withInputTestId(id, InputProps?.inputProps),
  };
}
